import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, ArrowsRightLeftIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface Transaction {
    direction: 'incoming' | 'outgoing' | 'internal';
    quantity_kg: number;
    item_type?: string;
    related_farmer_id?: number | null;
    related_cohort_id?: number | null;
}

interface Props {
    transactions: Transaction[];
}

const sumQty = (items: Transaction[]) => items.reduce((acc, t) => acc + Number(t.quantity_kg || 0), 0);

const StockWorkflowBoard: React.FC<Props> = ({ transactions }) => {
    const incoming = transactions.filter((t) => t.direction === 'incoming');
    const outgoing = transactions.filter((t) => t.direction === 'outgoing');
    const internal = transactions.filter((t) => t.direction === 'internal');

    const checks = [
        {
            label: 'Produce Received',
            ok: transactions.some((t) => t.direction === 'incoming' && (t.item_type || 'produce') === 'produce')
        },
        {
            label: 'Fertilizer Issued',
            ok: transactions.some((t) => t.direction === 'outgoing' && t.item_type === 'fertilizer')
        },
        {
            label: 'Compost Movement',
            ok: transactions.some((t) => (t.item_type === 'compost') && (t.direction === 'internal' || t.direction === 'outgoing' || t.direction === 'incoming'))
        },
        {
            label: 'Farmer Linkage',
            ok: transactions.some((t) => Boolean(t.related_farmer_id))
        },
        {
            label: 'Cohort Linkage',
            ok: transactions.some((t) => Boolean(t.related_cohort_id))
        }
    ];

    return (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Stock Workflow Coverage</h3>
                <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full">
                    Traceability Readiness
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Inflow</p>
                        <ArrowDownIcon className="w-4 h-4 text-emerald-700" />
                    </div>
                    <p className="text-xl font-bold text-emerald-900 mt-1">{sumQty(incoming).toFixed(1)} kg</p>
                    <p className="text-xs text-emerald-700">{incoming.length} transactions</p>
                </div>

                <div className="rounded-lg bg-rose-50 border border-rose-100 p-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-wide text-rose-700 font-semibold">Outflow</p>
                        <ArrowUpIcon className="w-4 h-4 text-rose-700" />
                    </div>
                    <p className="text-xl font-bold text-rose-900 mt-1">{sumQty(outgoing).toFixed(1)} kg</p>
                    <p className="text-xs text-rose-700">{outgoing.length} transactions</p>
                </div>

                <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3">
                    <div className="flex items-center justify-between">
                        <p className="text-xs uppercase tracking-wide text-indigo-700 font-semibold">Internal</p>
                        <ArrowsRightLeftIcon className="w-4 h-4 text-indigo-700" />
                    </div>
                    <p className="text-xl font-bold text-indigo-900 mt-1">{sumQty(internal).toFixed(1)} kg</p>
                    <p className="text-xs text-indigo-700">{internal.length} transactions</p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {checks.map((check) => (
                    <div key={check.label} className={`rounded-lg border p-2.5 flex items-center justify-between ${check.ok ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
                        <span className={`text-sm font-medium ${check.ok ? 'text-emerald-800' : 'text-amber-800'}`}>{check.label}</span>
                        {check.ok ? (
                            <CheckCircleIcon className="w-4 h-4 text-emerald-700" />
                        ) : (
                            <ExclamationTriangleIcon className="w-4 h-4 text-amber-700" />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default StockWorkflowBoard;

